import type {
  ActionItemVm,
  GoalVm,
  ItemDecision,
  MeasureVm,
} from './care-plans.contract';

export const CARE_PLAN_REVIEW_SYNC_EVENT = 'eluve:care-plan-review-sync';

export interface CarePlanReviewSyncDetail {
  tenantId: string;
  appointmentId: string;
  source: 'care-plan' | 'findings';
}

/**
 * The single encoding of "does this review row need a clinician verdict" —
 * shared by the api-nest view model (checklist / activation counts) and the
 * review-tab UI (confirmable rows, All-confirmed pills, tally) so the two can
 * never disagree.
 */
export type ReviewPosture = { reviewable: boolean; decision: ItemDecision };

export const itemReviewPosture = (
  item: Pick<GoalVm | ActionItemVm, 'itemState' | 'decision'>,
): ReviewPosture => ({
  reviewable: item.itemState !== 'unchanged',
  decision: item.decision,
});

/** A measure row has two verdict subjects — the this-visit capture and an
 *  AI-proposed target change — folded into one row posture. A pending target
 *  change always leaves the row pending; otherwise the capture's decision
 *  stands, falling back to the target change's rejected state. */
export const measureReviewPosture = (measure: MeasureVm): ReviewPosture => {
  const captureReviewable = Boolean(
    measure.thisVisit && measure.thisVisit.itemState !== 'unchanged',
  );
  const reviewable = captureReviewable || measure.targetChange !== null;
  if (measure.targetChange?.decision === 'pending') {
    return { reviewable, decision: 'pending' };
  }
  if (captureReviewable && measure.thisVisit) {
    return { reviewable, decision: measure.thisVisit.decision };
  }
  if (measure.targetChange) {
    return {
      reviewable,
      decision:
        measure.targetChange.decision === 'rejected' ? 'rejected' : 'approved',
    };
  }
  return { reviewable, decision: 'pending' };
};

export const needsVerdict = (posture: ReviewPosture): boolean =>
  posture.reviewable && posture.decision === 'pending';

const isRowConfirmed = (posture: ReviewPosture): boolean =>
  !posture.reviewable ||
  posture.decision === 'approved' ||
  posture.decision === 'edited';

export const allRowsConfirmed = (postures: ReviewPosture[]): boolean =>
  postures.length > 0 && postures.every(isRowConfirmed);
